<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import { computed, ref, watch } from 'vue';

import {
  useActiveIdentity,
  useAccessControlStore,
  useCustomerConfig,
  useHubspotChat,
  usePhoneSourceStore,
} from '@peanuds/app-shell';
import { getPhoneCountryOptions, splitPhoneNumber, type PhoneCountryOption } from '@peanuds/shared';
import {
  UiButton,
  UiDialogCloseButton,
  UiDialogShell,
  UiOtpVerification,
  UiPhoneNumberField,
} from '@peanuds/ui';

import { storeToRefs } from 'pinia';

interface Props {
  visible: boolean;
}

defineOptions({ name: 'TotalBalancePhoneVerificationDialog' });

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();

const { t } = useTranslation();

const phoneSourceStore = usePhoneSourceStore();
const accessControlStore = useAccessControlStore();
const activeIdentity = useActiveIdentity();
const { countries } = useCustomerConfig();
const { openChat } = useHubspotChat();
const { phone, isPhoneVerified, challengeErrorCode } = storeToRefs(phoneSourceStore);
const { initiatePhoneChallenge, resetPhoneState, verifyPhoneChallenge } = phoneSourceStore;
const { ensureAccessResolved } = accessControlStore;

const phoneCountry = ref<PhoneCountryOption | null>(null);
const phoneNumber = ref('');
const isSubmitting = ref(false);
const errorMessage = ref<string | null>(null);
const otpResetKey = ref(0);

const fullPhoneNumber = computed(() => {
  const countryCode = phoneCountry.value?.phone ?? '';
  const localNumber = phoneNumber.value.trim();

  if (!countryCode || localNumber.length === 0) {
    return null;
  }

  return `${countryCode}${localNumber}`;
});

const isOtpStepVisible = computed(() => {
  return !!phone.value.data?.challengeId;
});

const isSendCodeDisabled = computed(() => {
  return isSubmitting.value || fullPhoneNumber.value === null;
});

const phoneCountryOptions = computed(() => {
  return getPhoneCountryOptions(countries.value.phone);
});

const syncPhoneFields = () => {
  const rawPhone =
    phone.value.data?.phoneNumber?.trim() ?? activeIdentity.phoneNumber.value?.trim() ?? '';

  if (!rawPhone) {
    phoneCountry.value = null;
    phoneNumber.value = '';
    return;
  }

  const { phoneCountry: matchedCountry, phoneNumber: matchedPhoneNumber } =
    splitPhoneNumber(rawPhone);

  phoneCountry.value = matchedCountry;
  phoneNumber.value = matchedPhoneNumber;
};

const resetDialogState = () => {
  isSubmitting.value = false;
  errorMessage.value = null;
  otpResetKey.value += 1;

  if (!isPhoneVerified.value) {
    resetPhoneState();
  }

  syncPhoneFields();
};

const closeDialog = () => {
  emit('update:visible', false);
};

const resolveChallengeErrorMessage = (fallbackKey: string) => {
  const code = challengeErrorCode.value;
  const fallback = t(fallbackKey);

  if (!code) {
    return fallback;
  }

  const errorKey = code.toLowerCase().replace(/\./g, '_');

  return t(`totalBalance.phone_verification.errors.${errorKey}`, { defaultValue: fallback });
};

const requestPhoneVerificationCode = async () => {
  if (!fullPhoneNumber.value) {
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;

  try {
    const response = await initiatePhoneChallenge(fullPhoneNumber.value);

    if (!response) {
      errorMessage.value = resolveChallengeErrorMessage(
        'totalBalance.phone_verification.send_code_failed',
      );
      return;
    }

    otpResetKey.value += 1;
  } finally {
    isSubmitting.value = false;
  }
};

const handleOtpConfirm = async (otp: string) => {
  isSubmitting.value = true;

  try {
    const response = await verifyPhoneChallenge(otp);

    if (!response) {
      errorMessage.value = resolveChallengeErrorMessage(
        'totalBalance.phone_verification.invalid_code',
      );
      return;
    }

    errorMessage.value = null;
    await ensureAccessResolved();
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      errorMessage.value = null;
      syncPhoneFields();
      return;
    }

    resetDialogState();
  },
);

watch([phoneCountry, phoneNumber], () => {
  if (!isOtpStepVisible.value) {
    errorMessage.value = null;
  }
});

watch(isPhoneVerified, (nextValue) => {
  if (props.visible && nextValue) {
    closeDialog();
  }
});
</script>

<template>
  <UiDialogShell
    :visible="visible"
    :dismissable-mask="false"
    :aria-label="t('totalBalance.phone_verification.aria_label')"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #surface>
      <div class="phone-verification" data-testid="phone-verification-dialog">
        <template v-if="!isOtpStepVisible">
          <div class="phone-verification__container">
            <UiDialogCloseButton label="Close dialog" @click="closeDialog" />

            <div class="phone-verification__header">
              <h3 class="phone-verification__title">Verify your phone number</h3>
              <p class="phone-verification__subtitle">
                Enter your phone number to receive a verification code and continue onboarding.
              </p>
            </div>

            <UiPhoneNumberField
              v-model:country="phoneCountry"
              v-model:number="phoneNumber"
              label="Phone Number"
              class="phone-verification__phone-field"
              :disabled="isSubmitting"
              :options="phoneCountryOptions"
            />

            <div class="phone-verification__action-section">
              <p class="phone-verification__action-label">Didn’t receive a code?</p>

              <UiButton
                label="Send Verification Code"
                class="phone-verification__submit"
                data-testid="phone-verification-send-code"
                :loading="isSubmitting"
                :disabled="isSendCodeDisabled"
                @click="requestPhoneVerificationCode"
              />

              <small
                v-if="errorMessage"
                class="phone-verification__error phone-verification__error--bottom"
              >
                {{ errorMessage }}
              </small>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            class="phone-verification__container phone-verification__container--otp"
            data-testid="phone-verification-otp-step"
          >
            <UiOtpVerification
              appearance="dialog"
              cancel-label=""
              :reset-key="otpResetKey"
              @confirm="handleOtpConfirm"
              @cancel="closeDialog"
              @resend="requestPhoneVerificationCode"
              @support="openChat"
            />

            <small
              v-if="errorMessage"
              class="phone-verification__error phone-verification__error--otp"
            >
              {{ errorMessage }}
            </small>
          </div>
        </template>
      </div>
    </template>
  </UiDialogShell>
</template>

<style scoped lang="scss">
.phone-verification {
  position: relative;

  &__container {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-8;
    padding: 5rem $static-space-10 $static-space-10;

    &--otp {
      gap: $static-space-2;
      padding: 0 0 $static-space-10;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-4;
    width: 100%;
  }

  &__title {
    margin: 0;
    color: var(--p-surface-900);
    font-size: $static-text-xl;
    font-weight: $font-weight-semibold;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
    text-align: center;
  }

  &__subtitle {
    margin: 0;
    color: var(--p-surface-600);
    font-size: $static-text-sm;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
    text-align: center;
  }

  &__submit {
    width: fit-content;
    height: $static-space-8;
    font-size: $static-text-sm;
    font-weight: $font-weight-medium;
    letter-spacing: $tracking-tight;
    padding: 0 0.65625rem;
  }

  &__action-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-3;
  }

  &__action-label {
    width: 21.1875rem;
    margin: 0;
    color: var(--p-surface-900);
    font-size: $static-text-sm;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
    text-align: center;
  }

  &__phone-field {
    width: 100%;

    :deep(.ui-phone-number-field__label) {
      padding-left: $static-space-3;
      color: var(--p-surface-900);
      font-size: $static-text-sm;
      font-weight: $font-weight-medium;
      line-height: $leading-none;
      letter-spacing: $tracking-tight;
    }

    :deep(.ui-phone-number-field__inputgroup) {
      gap: $static-space-3;
    }

    :deep(.ui-phone-number-field__country-wrapper) {
      flex: 0 0 5.625rem;
      width: 5.625rem;
    }

    :deep(.ui-phone-number-field__country-select) {
      width: 100%;
    }

    :deep(.ui-phone-number-field__country-select.p-select),
    :deep(.ui-phone-number-field__input) {
      height: $static-space-10;
      border-color: var(--p-surface-300);
      border-radius: $radius-12;
      background: var(--p-surface-0);
      box-shadow: 0 1px 2px 0 rgba(18, 18, 23, 0.05);
    }
  }

  &__error {
    color: var(--p-red-500);
    font-size: $text-sm;
    line-height: $leading-normal;

    &--bottom,
    &--otp {
      display: block;
      padding: $static-space-2 $static-space-4;
      text-align: center;
    }
  }
}
</style>
